import { Outlet, NavLink } from 'react-router-dom'
import { Button } from '@/common/ui/button'
import { ArrowLeft, Menu } from 'lucide-react'
import { Logo, LogoMobile } from '@/common/assets/svg'
import { FullScreenLoader } from '@/common/ui/FullScreenLoader'
import IntercomVisibilityController from '@/common/ui/IntercomVisibilityController'
import { AdviserDesktopSideNav } from '@/common/navigation/AdviserDesktopSideNav'
import { AdviserMobileSlideNav } from '@/common/navigation/AdviserMobileSlideNav'
import { AppNav } from '@/common/navigation/AppNav'
import { UnifiedMobileBottomNav } from '@/common/navigation/UnifiedMobileBottomNav'
import { MobileInstrumentSearchDialog } from '@/common/navigation/MobileInstrumentSearchDialog'
import { MobileIntercomButton } from '@/common/intercom/MobileIntercomButton'
import { Footer } from '@/common/ui/Footer'
import ProfileDropdown from '@/common/navigation/ProfileDropdown'
import ToDoDropdown from '@/common/navigation/ToDoDropdown'
import InstrumentSearch from '@/common/navigation/InstrumentSearch'
import { BackButtonClickedAnalytics } from '@/common/analytics/events/BackButtonClickedAnalytics'
import type { AppShellVisibility } from './appShellVisibility'
import type { User } from '@/common/types'

export type PageProps = {
    user: User | null
    activeAccountId: string | null
    rewardsCount: number
    showAdviserChrome: boolean
    appShellVisibility: AppShellVisibility
    canGoBack: boolean
    isAdviserMenuOpen: boolean
    loading?: boolean
}

interface ViewModelProps extends PageProps {
    /** Back button click handler — *required for production use*; may be omitted in Storybook previews. */
    onBackClick?: () => void
    /** Adviser menu toggle handler — *required for production use*; may be omitted in Storybook previews. */
    onAdviserMenuToggle?: (open: boolean) => void
}

export function AppShell({
    user,
    activeAccountId,
    rewardsCount,
    showAdviserChrome,
    appShellVisibility,
    canGoBack,
    isAdviserMenuOpen,
    loading = false,
    onBackClick,
    onAdviserMenuToggle,
}: ViewModelProps) {
    if (loading) return <FullScreenLoader dark />

    return (
        <div className="relative h-screen flex flex-col">
            <IntercomVisibilityController open={false} />
            {showAdviserChrome && (
                <div className="hidden md:block fixed top-0 left-0 h-screen z-20">
                    <AdviserDesktopSideNav />
                </div>
            )}
            <div
                data-app-topbar
                className={`fixed top-0 left-0 w-full h-14 py-2 md:h-20 flex items-center px-6 md:px-0 z-10 ${showAdviserChrome ? 'bg-dust-50 md:left-16 md:w-[calc(100%-4rem)]' : 'bg-white'}`}
            >
                <div
                    className={`${canGoBack ? 'hidden md:flex' : 'flex'} items-center gap-6 w-[280px] md:min-w-[260px] md:w-[25vw] md:max-w-[330px] md:pl-12`}
                >
                    <div className="hidden md:block w-[100px]">
                        <NavLink to="/" className={`${showAdviserChrome ? 'invisible pointer-events-none' : ''}`}>
                            <Logo width={100} height={30} />
                        </NavLink>
                    </div>
                    <div className={`${canGoBack ? 'hidden' : 'block'} md:hidden`}>
                        <NavLink to="/">
                            <Logo width={100} height={30} className="app-topbar-logo-full" />
                            <LogoMobile width={32} height={31} className="app-topbar-logo-compact" />
                        </NavLink>
                    </div>
                </div>
                {canGoBack && (
                    <div className="block md:ml-6">
                        <Button
                            variant="tertiary"
                            size="icon"
                            onClick={() => {
                                BackButtonClickedAnalytics.track()
                                onBackClick?.()
                            }}
                            className="text-content-secondary"
                            aria-label="Back"
                        >
                            <ArrowLeft size={16} />
                        </Button>
                    </div>
                )}
                <div className="flex items-center gap-4 md:gap-2 ml-auto md:pr-12">
                    {appShellVisibility.showInstrumentSearch && (
                        <span className="hidden md:block">
                            <InstrumentSearch />
                        </span>
                    )}
                    {!showAdviserChrome && !appShellVisibility.isFullViewRoute && <ToDoDropdown />}
                    <MobileIntercomButton />
                    {appShellVisibility.showInstrumentSearch && <MobileInstrumentSearchDialog />}
                    <ProfileDropdown user={user} rewardsCount={rewardsCount} activeAccountId={activeAccountId} />
                    {showAdviserChrome && (
                        <button
                            className="block md:hidden p-1 text-content-primary"
                            aria-label="Open adviser menu"
                            onClick={() => onAdviserMenuToggle?.(true)}
                        >
                            <Menu size={24} />
                        </button>
                    )}
                </div>
            </div>

            <div className="flex flex-grow relative pt-14 md:pt-20">
                <div
                    className={`hidden md:block sticky top-14 md:top-20 h-[calc(100vh-80px)] ${showAdviserChrome ? 'bg-dust-50 md:ml-16' : ''} ${appShellVisibility.isFullViewRoute ? 'w-4' : 'min-w-[260px] w-1/4 max-w-[330px]'}`}
                >
                    {!appShellVisibility.isFullViewRoute && <AppNav />}
                </div>

                <main className="relative flex-grow overflow-y-auto">
                    <div
                        id="appContainer"
                        className={`max-w-screen-xl px-6 py-8 md:py-6 md:pl-8 md:pr-12 ${appShellVisibility.showMobileBottomNav ? 'pb-28' : ''}`}
                    >
                        <Outlet />
                        {appShellVisibility.showFooter && <Footer />}
                    </div>
                </main>
            </div>

            {appShellVisibility.showMobileBottomNav && <UnifiedMobileBottomNav />}
            {showAdviserChrome && (
                <AdviserMobileSlideNav open={isAdviserMenuOpen} onClose={() => onAdviserMenuToggle?.(false)} />
            )}
        </div>
    )
}
